import { Component, OnInit, inject, signal, computed, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal'; // Added modal module
import { NzButtonModule } from 'ng-zorro-antd/button'; // Added button module
import { NzTooltipModule } from 'ng-zorro-antd/tooltip'; // Added tooltip module

import { AuthStore } from '@rapider/authentication';
import { ProjectModelControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import { ProjectDataActions, ProjectDataService } from '@rapider/project-data';

import {
  DATABASE_CONFIG,
  buildProjectModelsFilter,
  groupAndSortModels,
  toggleRecordState,
  setAllRecordStates,
  extractDisplayedModelIds, // Added pure function
  ProjectModelGroup
} from '../database.config';

/**
 * @class DatabaseListComponent
 * @description Renders a filtered, searchable, and grouped list of database models for the active project.
 */
@Component({
  selector: 'rapider-database-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTagModule,
    NzInputModule,
    NzModalModule,
    NzButtonModule,
    NzTooltipModule
  ],
  templateUrl: './database-list.component.html',
  host: {
    class: 'block h-full w-full bg-body-background overflow-y-auto custom-scrollbar'
  }
})
export class DatabaseListComponent implements OnInit {
  private readonly projectModelController = inject(ProjectModelControllerService);
  private readonly notificationService = inject(NotificationService);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly store = inject(Store); // Added Store injection
  private readonly projectDataService = inject(ProjectDataService);

  public readonly config = DATABASE_CONFIG;

  // Active Data Signal
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public models = signal<any[]>([]);

  // Base Computed Signal resolving grouped tables ready for presentation
  private groupedModels = computed(() => groupAndSortModels(this.models()));

  // Interaction UI State
  public isLoading = signal<boolean>(true);
  public searchQuery = signal<string>('');
  public selectedDomain = signal<string | null>(null);
  public expandedGroups = signal<Record<string, boolean>>({});

  // AI Context Modal Signals
  public isAiContextModalVisible = signal<boolean>(false);
  public isAiContextLoading = signal<boolean>(false);
  public aiContextContent = signal<string>('');
  public isCopied = signal<boolean>(false);

  // Derived Computed Signal extracting unique domains for the filter tags
  public uniqueDomains = computed(() => {
    return this.groupedModels().map((g: ProjectModelGroup) => ({ key: g.domainKey, name: g.domainName }));
  });

  // Derived Computed Signal applying search and domain filters to the groups
  public filteredGroupedModels = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const domain = this.selectedDomain();
    let groups: ProjectModelGroup[] = this.groupedModels();

    if (domain) {
      groups = groups.filter((g: ProjectModelGroup) => g.domainKey === domain);
    }

    if (query) {
      groups = groups.map((g: ProjectModelGroup) => ({
        ...g,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        models: g.models.filter((m: any) =>
          (m.title && m.title.toLowerCase().includes(query)) ||
          (m.dataTableName && m.dataTableName.toLowerCase().includes(query)) ||
          (m.description && m.description.toLowerCase().includes(query))
        )
      })).filter((g: ProjectModelGroup) => g.models.length > 0);
    }

    return groups;
  });

  // Computed Signal to determine if all visible groups are expanded
  public areAllGroupsExpanded = computed(() => {
    const groups = this.filteredGroupedModels();
    if (groups.length === 0) return true;
    return groups.every((g: ProjectModelGroup) => this.expandedGroups()[g.domainKey] !== false);
  });

  constructor() {
    /**
     * EFFECT: Reactively reload data if the active project changes (e.g., on page refresh).
     * This solves the issue where AuthStore might not be populated instantly on load.
     */
    effect(() => {
      const activeProject = this.authStore.activeProject();
      const projectId = activeProject?.id;

      // Execute the state update outside the reactive context to prevent infinite effect loops
      untracked(() => {
        if (projectId) {
          this.fetchModels(projectId);
        } else {
          this.models.set([]);
          this.isLoading.set(false);
        }
      });
    });
  }

  ngOnInit(): void {
    // Model fetching logic is now safely handled by the reactive effect in the constructor.
  }

  /**
   * Fetches models directly assigned to the current project using a field-optimized payload.
   * Utilizes the centralized project data service to ensure the NgRx store receives the cached data correctly.
   * @param {string} projectId - The active project ID string.
   */
  private fetchModels(projectId: string): void {
    this.isLoading.set(true);

    this.projectDataService.fetchAndCacheLimitedProjectModelsList(projectId).subscribe({
      next: (res: any[]) => {
        this.models.set(res);

        // Initialize all groups as expanded by default
        const initialExpandedState: Record<string, boolean> = {};
        this.groupedModels().forEach((g: ProjectModelGroup) => initialExpandedState[g.domainKey] = true);
        this.expandedGroups.set(initialExpandedState);

        this.isLoading.set(false);
      },
      error: () => {
        this.notificationService.createNotification('error', 'Error', this.config.notifications.fetchFailed);
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Toggles the collapsed/expanded state of a specific domain group.

  /**
   * Toggles the collapsed/expanded state of a specific domain group.
   * @param {string} domainKey - Target domain key identifier.
   */
  public toggleGroup(domainKey: string): void {
    this.expandedGroups.update(state => toggleRecordState(state, domainKey));
  }

  /**
   * Toggles all visible domain groups between fully expanded and fully collapsed.
   */
  public toggleAllGroups(): void {
    const targetState = !this.areAllGroupsExpanded();
    this.expandedGroups.update(state => setAllRecordStates(state, targetState));
  }

  /**
   * Updates the selected domain filter. Null indicates "All Domains".
   * @param {string | null} domainKey - The key of the domain to filter by.
   */
  public selectDomain(domainKey: string | null): void {
    this.selectedDomain.set(domainKey);
  }

  /**
     * Router bridge triggering navigation into detailed table inspection.
     * @param {string} modelId - Target model string identifier.
     */
  public goToDetails(modelId: string): void {
    this.router.navigate(['/database', modelId]);
  }

  /**
   * Opens the AI Context Modal and fetches the structured schemas strictly for the displayed models.
   * Leverages the SDK to securely fetch the compiled data context string natively.
   */
  public openAiContextModal(): void {
    this.isAiContextModalVisible.set(true);
    this.isAiContextLoading.set(true);
    this.aiContextContent.set('');
    this.isCopied.set(false);

    // Utilize pure function to determine exact model IDs needed based on current view filters
    const displayedIds = extractDisplayedModelIds(this.searchQuery(), this.selectedDomain(), this.filteredGroupedModels());

    this.projectModelController.getDescriptionsForAiContext({ projectModelIds: displayedIds }).subscribe({
      next: (context: string) => {
        this.aiContextContent.set(context);
        this.isAiContextLoading.set(false);
      },
      error: () => {
        this.notificationService.createNotification('error', 'Error', this.config.notifications.fetchFailed);
        this.isAiContextLoading.set(false);
        this.isAiContextModalVisible.set(false);
      }
    });
  }

  /**
   * Natively closes the AI Context Modal.
   */
  public closeAiContextModal(): void {
    this.isAiContextModalVisible.set(false);
  }

  /**
   * Copies the retrieved AI Context text directly to the user's system clipboard using standard browser APIs.
   * Momentarily toggles a visual feedback signal.
   */
  public async copyAiContextToClipboard(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.aiContextContent());
      this.isCopied.set(true);
      setTimeout(() => this.isCopied.set(false), 2000);
    } catch (err) {
      this.notificationService.createNotification('error', 'Error', 'Failed to copy to clipboard. Your browser might block clipboard access.');
    }
  }
}